import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
const FavoritesPage = ({ API, userToken, isDarkMode, showNotification, setCurrentView }) => {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await fetch(`${API}/favorites`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (id) => {
    try {
      const response = await fetch(`${API}/favorites/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        setFavorites(favorites.filter(f => f.id !== id));
        showNotification('✓ ' + t('fav_removed_ok'), 'success');
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      showNotification(t('fav_remove_failed'), 'error');
    }
  };

  const playAudio = async (text, audioUrl = null) => {
    try {
      if (audioUrl && audioUrl.trim()) {
        const audio = new Audio(audioUrl);
        audio.play().catch(err => {
          console.error('Audio playback failed:', err);
        });
      } else if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ar-MA';
        utterance.rate = 0.8;
        speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Audio error:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {setCurrentView && (
        <button onClick={() => setCurrentView('translator')}
          className={`flex items-center gap-1 mb-3 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('back')}
        </button>
      )}
      <h1 className={`text-3xl font-bold mb-6 bg-gradient-to-r from-pink-600 to-red-600 bg-clip-text text-transparent`}>
        ❤️ {t('fav_my_favorites')}
      </h1>

      {loading ? (
        <div className="text-center py-12">
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{t('loading')}</p>
        </div>
      ) : favorites.length === 0 ? (
        <div className={`text-center py-20 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-pink-50 to-red-50'}`}>
          <div className="text-6xl mb-4">❤️</div>
          <p className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {t('fav_no_yet')}
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('fav_save_to_love')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {favorites.map((fav, index) => (
            <div
              key={fav.id}
              className={`p-4 rounded-xl border-2 ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Audio Button */}
                {fav.audioUrl && (
                  <button
                    onClick={() => playAudio(fav.translatedText, fav.audioUrl)}
                    className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? 'text-blue-400 hover:bg-gray-700'
                        : 'text-blue-600 hover:bg-blue-50'
                    }`}
                    title={t('fav_play_audio')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                  </button>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      #{index + 1}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {fav.sourceLang ? fav.sourceLang.toUpperCase() : 'EN'}
                    </span>
                  </div>
                  
                  <p className={`text-lg font-medium mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    "{fav.sourceText}"
                  </p>
                  
                  <p className={`text-base ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    → {fav.translatedText}
                  </p>
                  
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('fav_added_prefix')}: {new Date(fav.createdAt).toLocaleDateString()} {new Date(fav.createdAt).toLocaleTimeString()}
                  </p>
                </div>
                
                <button
                  onClick={() => removeFavorite(fav.id)}
                  className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title={t('fav_remove_title')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default FavoritesPage;
